import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, getNotificationText, getNotificationIcon } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bell, 
  Ticket, 
  UserCheck, 
  Clock, 
  XCircle, 
  CheckCircle, 
  UserPlus,
  Check
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ICON_MAP = {
  'ticket': Ticket,
  'user-check': UserCheck,
  'clock': Clock,
  'x-circle': XCircle,
  'check-circle': CheckCircle,
  'user-plus': UserPlus,
  'bell': Bell,
};

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, connected } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.data?.ticket_id) {
      navigate(`/ticket/${notification.data.ticket_id}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {connected && (
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full"></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-indigo-600 hover:text-indigo-700"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-96">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const IconComponent = ICON_MAP[getNotificationIcon(notification.type)] || Bell;
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-indigo-50' : ''
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.type.includes('rejected') ? 'bg-red-100' :
                        notification.type.includes('approved') || notification.type.includes('resolved') ? 'bg-green-100' :
                        notification.type.includes('approval') ? 'bg-orange-100' :
                        'bg-indigo-100'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${
                          notification.type.includes('rejected') ? 'text-red-600' :
                          notification.type.includes('approved') || notification.type.includes('resolved') ? 'text-green-600' :
                          notification.type.includes('approval') ? 'text-orange-600' :
                          'text-indigo-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {getNotificationText(notification)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No notifications yet</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
